import React from "react";
import LazyLoad from "react-lazyload";
import Seo from "../components/Seo";
import teamchallnages from "../finalist_photos/finalsteamchallange-min.webp";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MenuBookIcon from "@mui/icons-material/MenuBook";

function AboutIRO() {
  const navigate = useNavigate();

  const goToPracticeExams = () => {
    navigate("/practice-exams");
  };

  const ExamPhaseCard = ({
    title,
    date,
    location,
    curriculumLink,
    description,
    highlight,
  }) => (
    <div
      style={{
        background: highlight
          ? "linear-gradient(135deg, rgba(0, 77, 153, 0.05) 0%, rgba(0, 77, 153, 0.02) 100%)"
          : "#fff",
        borderRadius: "16px",
        padding: "32px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
        border: highlight ? "2px solid #004d99" : "1px solid #e8eef3",
        marginBottom: "24px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        {highlight && (
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "20px",
              backgroundColor: "#004d99",
              color: "#fff",
              fontSize: "0.75rem",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            Finals
          </span>
        )}
        <h3
          style={{
            fontSize: "1.5rem",
            fontWeight: "600",
            color: "#1a1a2e",
            margin: 0,
          }}
        >
          {title}
        </h3>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "24px",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <CalendarTodayIcon style={{ fontSize: "18px", color: "#004d99" }} />
          <span style={{ fontSize: "0.95rem", color: "#555" }}>{date}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <LocationOnIcon style={{ fontSize: "18px", color: "#004d99" }} />
          <span style={{ fontSize: "0.95rem", color: "#555" }}>{location}</span>
        </div>
        {curriculumLink && (
          <a
            href={curriculumLink}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "#004d99",
              textDecoration: "none",
              fontWeight: "500",
              fontSize: "0.95rem",
            }}
          >
            <MenuBookIcon style={{ fontSize: "18px" }} />
            View Curriculum
          </a>
        )}
      </div>

      <p
        style={{
          fontSize: "1rem",
          color: "#555",
          lineHeight: "1.8",
          margin: 0,
        }}
      >
        {description}
      </p>
    </div>
  );

  return (
    <div>
      <Seo
        title="About the Exam"
        path="/about-iro"
        description="How the International Research Olympiad exam works — the Opens, Semifinal, and Finals rounds that test scientific research, data interpretation, and critical thinking."
      />
      {/* Hero Section */}
      <div style={{ backgroundColor: "#f8f9fa", padding: "60px 0 50px 0" }}>
        <div className="container">
          <div style={{ maxWidth: "800px" }}>
            <h1
              style={{
                fontSize: "clamp(2rem, 4vw, 2.5rem)",
                fontWeight: "700",
                color: "#1a1a2e",
                marginBottom: "16px",
                letterSpacing: "-0.02em",
              }}
            >
              How to Prepare for IRO
            </h1>
            <p
              style={{
                fontSize: "1.1rem",
                color: "#555",
                lineHeight: "1.7",
              }}
            >
              Everything you need to know about the International Research
              Olympiad examinations and how to succeed.
            </p>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: "50px 0 80px 0" }}>
        {/* Video and Intro Section */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "40px",
            alignItems: "center",
            marginBottom: "60px",
          }}
        >
          <div style={{ flex: "1", minWidth: "300px" }}>
            <div
              style={{
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
              }}
            >
              <iframe
                width="100%"
                height="315"
                src="https://www.youtube.com/embed/N3jywbAqfNU?si=RghsjMZCMK-o7-AT"
                title="How to Prepare for IRO"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ display: "block" }}
              />
            </div>
          </div>
          <div style={{ flex: "1", minWidth: "300px" }}>
            <h2
              style={{
                fontSize: "1.75rem",
                fontWeight: "600",
                color: "#1a1a2e",
                marginBottom: "16px",
              }}
            >
              A Different Kind of Competition
            </h2>
            <p
              style={{ fontSize: "1.05rem", color: "#555", lineHeight: "1.8" }}
            >
              The International Research Olympiad is a competition that doesn't
              just rely on memorization of content or a specific type of
              problem. It aims to test crucial analytical skills that are
              essential to succeed as a <strong>researcher</strong>.
            </p>
          </div>
        </div>

        {/* About the Exam Section */}
        <div style={{ marginBottom: "60px" }}>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "600",
              color: "#1a1a2e",
              marginBottom: "24px",
            }}
          >
            About the Exam
          </h2>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "40px",
              alignItems: "center",
              marginBottom: "40px",
            }}
          >
            <div style={{ flex: "2", minWidth: "300px" }}>
              <p
                style={{
                  fontSize: "1.05rem",
                  color: "#555",
                  lineHeight: "1.8",
                }}
              >
                The International Research Olympiad (IRO) is dedicated to honing
                analytical and critical thinking skills for student
                researchers—essential in scientific exploration. The IRO fosters
                a culture of rigorous research and skill-building, not just
                memorizing formulae. The practical applications of knowledge and
                big-picture thinking are tested in the IRO examinations,
                allowing students to walk away with a deeper passion for
                scientific inquiry.
              </p>
            </div>
            <div style={{ flex: "1", minWidth: "250px" }}>
              <LazyLoad height={200} offset={100} once>
                <img
                  src={teamchallnages}
                  alt="IRO Team Challenge"
                  style={{
                    width: "100%",
                    maxWidth: "350px",
                    borderRadius: "16px",
                    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
                  }}
                />
              </LazyLoad>
            </div>
          </div>

        </div>

        {/* Exam Phases */}
        <div style={{ marginBottom: "60px" }}>
          <h2
            style={{
              fontSize: "2rem",
              fontWeight: "600",
              color: "#1a1a2e",
              marginBottom: "32px",
            }}
          >
            Competition Phases
          </h2>

          <ExamPhaseCard
            title="Opens"
            date="Online | 8 AM & 8 PM ET"
            location="Online"
            curriculumLink="/curriculum"
            description="The 'Opens' round is a comprehensive assessment, encompassing diverse elements such as analyzing research papers, interpreting scientific data, and tackling question formation and inquiry. This stage tests participants' analytical skills and critical thinking. The Opens round consists mostly of multiple-choice questions. Registered students receive login information for accessing the IRO Open Exam via a portal on our website."
          />

          <ExamPhaseCard
            title="Semifinals"
            date="Virtual | 8 AM & 8 PM ET"
            location="Virtual"
            curriculumLink="/semi-curriculum"
            description="The top 10% of participants advance to the semifinals from the Open examinations. The semifinals elevate the challenge by introducing free-response questions judged by qualified researchers, delving deeper into participants' ability to articulate and apply their understanding of scientific concepts."
          />

          <ExamPhaseCard
            title="Finals"
            date="In-Person | Harvard Square"
            location="In-Person at Harvard Square"
            description="The pinnacle of the competition is the finals, where the top participants compete for the coveted gold, silver, and bronze medals. The IRO hosts in-person finals at Harvard Square with accommodation expenses covered for finalists. Additional sponsor prizes, media opportunities, and recognition are awarded."
            highlight={true}
          />
        </div>

        {/* Finals Video */}
        <div style={{ marginBottom: "60px" }}>
          <h2
            style={{
              fontSize: "1.75rem",
              fontWeight: "600",
              color: "#1a1a2e",
              marginBottom: "24px",
            }}
          >
            Experience the Finals
          </h2>
          <div
            style={{
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
            }}
          >
            <iframe
              style={{ width: "100%", height: "500px", display: "block" }}
              src="https://www.youtube.com/embed/LmKxVI8i3iU?si=P5BMrJcnarl3DaTX"
              title="IRO Finals"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>

        {/* CTA Section */}
        <div
          style={{
            background: "linear-gradient(135deg, #004d99 0%, #0066cc 100%)",
            borderRadius: "16px",
            padding: "40px",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: "1.75rem",
              fontWeight: "600",
              color: "#fff",
              marginBottom: "16px",
            }}
          >
            Ready to Start Preparing?
          </h2>
          <p
            style={{
              fontSize: "1.05rem",
              color: "rgba(255, 255, 255, 0.9)",
              marginBottom: "24px",
              maxWidth: "600px",
              margin: "0 auto 24px auto",
            }}
          >
            Access practice exams, study resources, and preparation materials to
            help you succeed at the IRO.
          </p>
          <button
            onClick={goToPracticeExams}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "14px 28px",
              backgroundColor: "#fff",
              color: "#004d99",
              border: "none",
              borderRadius: "10px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: "pointer",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            IRO Study Resources{" "}
            <ArrowForwardIcon style={{ fontSize: "20px" }} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default AboutIRO;
